import React, { createContext, useState, useEffect } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// 🔐 AUTH CONTEXT FOR MULTI-USER SYSTEM
export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // 👤 USER AUTHENTICATION STATE  
  const [authState, setAuthState] = useState({
    token: localStorage.getItem('subbuteo_token'),
    user: null,
    isAuthenticated: false,
    isLoading: true
  });

  useEffect(() => {
    // Check for token in both localStorage (persistent) and sessionStorage (session-only)
    const userToken = localStorage.getItem('subbuteo_token') || sessionStorage.getItem('subbuteo_token');
    
    console.log('🔍 AUTH CHECK: Token check on mount', { 
      hasToken: !!userToken, 
      tokenSource: userToken ? (localStorage.getItem('subbuteo_token') ? 'localStorage' : 'sessionStorage') : 'none' 
    });
    
    if (userToken) {
      verifyUserToken(userToken);
    } else {
      setAuthState(prev => ({ ...prev, isLoading: false }));
    }
  }, []);

  const verifyUserToken = async (token) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const userData = await response.json();
        setAuthState({
          token,
          user: userData.user,
          isAuthenticated: true,
          isLoading: false
        });
      } else if (response.status === 401) {
        // Only remove token on actual authentication failure (401)
        console.log('Token expired or invalid, removing from storage');
        localStorage.removeItem('subbuteo_token');
        sessionStorage.removeItem('subbuteo_token');
        localStorage.removeItem('subbuteo_remember');
        setAuthState({
          token: null,
          user: null,
          isAuthenticated: false,
          isLoading: false
        });
      } else {
        // For other errors (500, network issues), keep token and set authenticated anyway
        console.warn('Token verification failed with non-401 error, keeping token');
        setAuthState({
          token,
          user: null,
          isAuthenticated: true,
          isLoading: false
        });
      }
    } catch (error) {
      console.error('Token verification network error:', error);
      // For network errors, assume token is still valid and keep user logged in
      setAuthState({
        token,
        user: null,
        isAuthenticated: true,
        isLoading: false
      });
    }
  };

  // 👤 USER LOGIN FUNCTIONS
  const login = async (email, password, rememberMe = false) => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          email, 
          password, 
          remember_me: rememberMe 
        })
      });

      if (response.ok) {
        const data = await response.json();
        
        console.log('🔐 LOGIN API SUCCESS: Received data', { 
          hasToken: !!data.access_token, 
          hasUser: !!data.user,
          userEmail: data.user?.email 
        });
        
        // Store token based on remember_me preference
        if (rememberMe) {
          // Store in localStorage for persistent login
          localStorage.setItem('subbuteo_token', data.access_token);
          localStorage.setItem('subbuteo_remember', 'true');
        } else {
          // Store in sessionStorage for session-only login
          sessionStorage.setItem('subbuteo_token', data.access_token);
          localStorage.removeItem('subbuteo_remember');
        }
        
        console.log('🔐 TOKEN STORED: Setting auth state', { 
          token: data.access_token?.substring(0, 20) + '...', 
          user: data.user?.email,
          isAuthenticated: true 
        });
        
        setAuthState({
          token: data.access_token,
          user: data.user,
          isAuthenticated: true,
          isLoading: false
        });
        
        console.log('🔐 AUTH STATE SET: Login function completed');
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail || 'Login failed' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const register = async (email, name, password, promoCode = null) => {
    try {
      const requestBody = { email, name, password };
      if (promoCode && promoCode.trim()) {
        requestBody.promo_code = promoCode.trim();
      }

      const response = await fetch(`${BACKEND_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        const data = await response.json();
        localStorage.setItem('subbuteo_token', data.access_token);
        setAuthState({
          token: data.access_token,
          user: data.user,
          isAuthenticated: true,
          isLoading: false
        });
        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail || 'Registration failed' };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'Network error' };
    }
  };

  const logout = () => {
    // Remove token from both storages
    localStorage.removeItem('subbuteo_token');
    sessionStorage.removeItem('subbuteo_token');
    localStorage.removeItem('subbuteo_remember');
    setAuthState({
      token: null,
      user: null,
      isAuthenticated: false,
      isLoading: false
    });
  };

  return (
    <AuthContext.Provider value={{ 
      authState, 
      login, 
      register, 
      logout
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
